/**
 * Entity change helpers shared by every command handler:
 * undoable add/delete/modify + entity lookup + prompt plumbing.
 * All CAD mutations flow through the store API (A2 contract) so the scene
 * ledger stays mirrored.
 */

import { useAppStore } from '../../state/store';
import type { Entity } from '../../state/store';
import { useCommandInteract } from './commandInteract';
import { useLayoutStore } from '../../state/useLayoutStore';

/** Add entities as ONE undoable step; returns their ids. */
export function undoableAdd(entities: Entity[]): string[] {
    const app = useAppStore.getState();
    entities.forEach((e) => app.addEntity(e));
    const ids = entities.map((e) => e.id);
    app.pushHistory({
        type: 'cmd.add',
        timestamp: Date.now(),
        data: { count: ids.length },
        undo: () => { const a = useAppStore.getState(); ids.forEach((id) => a.deleteEntity(id)); },
        redo: () => { const a = useAppStore.getState(); entities.forEach((e) => a.addEntity(e)); },
    });
    return ids;
}

/** Delete entity ids as ONE undoable step. */
export function undoableDelete(ids: string[]): void {
    const app = useAppStore.getState();
    const snapshots = ids.map((id) => app.entities[id]).filter(Boolean) as Entity[];
    ids.forEach((id) => app.deleteEntity(id));
    app.pushHistory({
        type: 'cmd.delete',
        timestamp: Date.now(),
        data: { count: snapshots.length },
        undo: () => { const a = useAppStore.getState(); snapshots.forEach((e) => a.addEntity(e)); },
        redo: () => { const a = useAppStore.getState(); snapshots.forEach((e) => a.deleteEntity(e.id)); },
    });
}

/** Modify data of entity ids as ONE undoable step. */
export function undoableModify(ids: string[], mutate: (data: Record<string, any>, entity: Entity) => Record<string, any>): void {
    const app = useAppStore.getState();
    const before = new Map<string, Record<string, any>>();
    const after = new Map<string, Record<string, any>>();
    ids.forEach((id) => {
        const e = app.entities[id];
        if (!e) return;
        before.set(id, structuredClone(e.data));
        const next = mutate(structuredClone(e.data), e);
        after.set(id, next);
        useAppStore.getState().updateEntity(id, { data: next });
    });
    app.pushHistory({
        type: 'cmd.modify',
        timestamp: Date.now(),
        data: { count: ids.length },
        undo: () => { const a = useAppStore.getState(); before.forEach((d, id) => a.updateEntity(id, { data: d })); },
        redo: () => { const a = useAppStore.getState(); after.forEach((d, id) => a.updateEntity(id, { data: d })); },
    });
}

/** Make a CAD drafting entity. In an active paper-space layout the entity
 *  is tagged so it draws on the sheet (Faz 9) instead of in model space. */
export function makeEntity(type: string, name: string, data: Record<string, any>, layer?: string): Entity {
    const app = useAppStore.getState();
    const layoutId = app.mode === '2d' ? useLayoutStore.getState().activeId : null;
    const tagged = layoutId && data.onSheet === undefined
        ? { ...data, onSheet: layoutId }
        : data;
    return {
        id: `cad:${crypto.randomUUID()}`,
        type,
        name: name || type,
        visible: true,
        locked: false,
        layer: layer ?? app.activeLayerId,
        data: tagged,
    };
}

/** Delete `oldIds` and add `newEntities` as ONE undoable step. */
export function undoableReplace(oldIds: string[], newEntities: Entity[]): string[] {
    const app = useAppStore.getState();
    const snapshots = oldIds.map((id) => app.entities[id]).filter(Boolean) as Entity[];
    oldIds.forEach((id) => app.deleteEntity(id));
    newEntities.forEach((e) => app.addEntity(e));
    const newIds = newEntities.map((e) => e.id);
    app.pushHistory({
        type: 'cmd.replace',
        timestamp: Date.now(),
        data: { count: newIds.length },
        undo: () => {
            const a = useAppStore.getState();
            newIds.forEach((id) => a.deleteEntity(id));
            snapshots.forEach((e) => a.addEntity(e));
        },
        redo: () => {
            const a = useAppStore.getState();
            snapshots.forEach((e) => a.deleteEntity(e.id));
            newEntities.forEach((e) => a.addEntity(e));
        },
    });
    return newIds;
}

/** Polyline entity from points. */
export function polylineEntity(points: [number, number, number][], opts: { name?: string; closed?: boolean; color?: string; layer?: string; type?: string } = {}) {
    return makeEntity(opts.type ?? 'polyline', opts.name ?? `Polyline-${Date.now()}`, {
        vertices: points.map((p) => [p[0], p[1], p[2] ?? 0]),
        closed: !!opts.closed,
        color: opts.color ?? '#ffffff',
    }, opts.layer);
}

/** Line entity. */
export function lineEntity(a: [number, number, number], b: [number, number, number], color = '#ffffff') {
    return makeEntity('line', `Line-${Date.now()}`, { start: a, end: b, color });
}

/** Start an interaction spec (the mounted CommandInteractScope collects points). */
export function startInteraction(spec: import('./commandInteract').InteractionSpec): void {
    useCommandInteract.getState().start(spec);
}
